// The Customers area's view model (SCR-003 list, SCR-004 customer page): tabs and who may open
// them, the list's rows and filters, the header, the Usage and People tabs, and the forms
// of the AWS accounts, Sign-in and Settings tabs. Every function here is pure and DOM-free
// (tests/unit/orgs.test.ts); data fetching and rendering live in the .tsx files, which read the
// API's generated types (`src/api/schema.ts`) through these aliases — no endpoint is invented.
import type { ApiError } from "@nexus/ui";
import type { components } from "../../api/schema";
import { buildPath } from "../../router/router";
import type { Session } from "../../session/session";
import type { ChartBar } from "@nexus/ui";
import type { ChipTone, TabLink } from "@nexus/ui";
import { fieldErrorsFrom, hasErrors, integerBetween, required, validate, type FieldErrors, type Rule } from "@nexus/ui";
import { formatUsd } from "@nexus/ui";
import { monthLabel, MONTH_SHORT_NAMES } from "@nexus/ui";

export type Org = components["schemas"]["Org"];
export type OrgStatus = components["schemas"]["OrgStatus"];
export type CreateOrgRequest = components["schemas"]["CreateOrgRequest"];
export type PatchOrgRequest = components["schemas"]["PatchOrgRequest"];
export type Installation = components["schemas"]["Installation"];
export type CreateInstallationRequest = components["schemas"]["CreateInstallationRequest"];
export type OidcProvider = components["schemas"]["OidcProvider"];
export type CreateOidcProviderRequest = components["schemas"]["CreateOidcProviderRequest"];
export type PatchOidcProviderRequest = components["schemas"]["PatchOidcProviderRequest"];
export type AwsSignInSettings = components["schemas"]["AwsSignInSettings"];
export type AwsTeamMapping = components["schemas"]["AwsTeamMapping"];
export type OrgSettings = components["schemas"]["OrgSettings"];
export type PatchOrgSettingsRequest = components["schemas"]["PatchOrgSettingsRequest"];
export type CostVisibility = components["schemas"]["CostVisibility"];
export type PutOrgCostVisibilityRequest = components["schemas"]["PutOrgCostVisibilityRequest"];
export type OrgUsageItem = components["schemas"]["OrgUsageItem"];
export type ModelUsageItem = components["schemas"]["ModelUsageItem"];
export type RevenueBreakdown = components["schemas"]["RevenueBreakdown"];
export type TimeseriesPoint = components["schemas"]["TimeseriesPoint"];
export type TimeseriesResponse = components["schemas"]["TimeseriesResponse"];
export type User = components["schemas"]["User"];
export type Team = components["schemas"]["Team"];
export type UsageTotals = Pick<
  components["schemas"]["UsageSummaryResponse"],
  "requests" | "errors" | "inputTokens" | "outputTokens" | "cacheReadTokens" | "cacheWriteTokens" | "costMicroUsd"
>;

// ---------------------------------------------------------------------------------------------
// Tabs, routing and who may open what (NAV-003)

export const ORGS_PATH = "/customers";
/** A customer tab (NAV-038): `/customers/:orgId/:tab`, which shows the tab's first visible sub-tab. */
export const ORG_DETAIL_PATH = "/customers/:orgId/:tab";
/**
 * One of a tab's sub-tabs (NAV-042, ADR-115); the router has no optional segment, so it is a
 * second route. On the Quotas tab the segment is a quota's id: its detail, under the tab bar.
 */
export const ORG_SUBTAB_PATH = "/customers/:orgId/:tab/:subtab";
export const INSTALLATION_SETTINGS_PATH = "/customers/:orgId/installations/:installationId/settings";
/** NAV-024: one Assistant conversation (SCR-311), inside the customer page's Assistant tab. */
export const ASSISTANT_SESSION_PATH = "/customers/:orgId/assistant/:sessionId";

/** NAV-038's tabs, in the tab bar's order; every one renders inside the customer page (REQ-238). */
export const CUSTOMER_TABS = ["usage", "people", "aws-accounts", "sign-in", "keys", "models", "guardrails", "quotas", "notifications", "activity", "billing", "settings", "assistant"] as const;
export type CustomerTab = (typeof CUSTOMER_TABS)[number];

export const TAB_TITLES: Readonly<Record<CustomerTab, string>> = {
  usage: "Usage",
  people: "People",
  "aws-accounts": "AWS accounts",
  "sign-in": "Sign-in",
  keys: "Keys",
  models: "Models",
  guardrails: "Guardrails",
  quotas: "Quotas",
  notifications: "Notifications",
  activity: "Activity",
  billing: "Billing",
  settings: "Settings",
  assistant: "Assistant",
};

/** An unknown or missing `:tab` segment falls back to Usage rather than 404ing the customer. */
export function customerTabFrom(value: string | undefined): CustomerTab {
  return (CUSTOMER_TABS as readonly string[]).includes(value ?? "") ? (value as CustomerTab) : "usage";
}

/** A customer tab's address, or one of its sub-tabs' (`/customers/:orgId/:tab/:subtab`). */
export function customerPath(orgId: string, tab: CustomerTab = "usage", subtab?: string): string {
  return subtab === undefined ? buildPath(ORG_DETAIL_PATH, { orgId, tab }) : buildPath(ORG_SUBTAB_PATH, { orgId, tab, subtab });
}

/**
 * Who opens which tab; the customer routes need only a session, so this is the page's one gate.
 * A super-admin opens every tab. Billing needs `billing:read`. Usage is also
 * the org's own principals' with `reports:read` (NAV-018: an org admin's role grants it). Quotas
 * opens to staff with `billing:read` and to the org's own principals, and Notifications to the
 * org's own principals: their routes need only a session, and the screens decide write access
 * through the org context. Activity, the org's timeline (REQ-243), opens to the org's own admin,
 * the audience of API-588 beside a super-admin; its Prompt logs sub-tab decides its own presence
 * (`ACTIVITY_SUBTABS`, REQ-244), so no reader flag is needed for the tab. Every other tab reads
 * org-scoped APIs only a super-admin may call on any customer. The Assistant tab (NAV-024) is one
 * of those: in M0 API-310 admits staff only, which in this app is a super-admin (TASK-659).
 */
export function canOpenTab(session: Session, tab: CustomerTab, orgId: string): boolean {
  if (session.allows("super-admin")) return true;
  const ownOrg = session.me.orgId === orgId;
  switch (tab) {
    case "billing":
      return session.can("billing:read");
    case "quotas":
      return ownOrg || session.can("billing:read");
    case "notifications":
      return ownOrg;
    case "activity":
      return ownOrg && session.me.role === "org-admin";
    case "usage":
      return ownOrg && session.can("reports:read");
    default:
      return false;
  }
}

/** The tabs a session sees, in NAV-038's order. */
export function visibleTabs(session: Session, orgId: string): CustomerTab[] {
  return CUSTOMER_TABS.filter((tab) => canOpenTab(session, tab, orgId));
}

/** Where a customer's name links to: its first tab the session can open. */
export function defaultTab(session: Session, orgId: string): CustomerTab {
  return visibleTabs(session, orgId)[0] ?? "usage";
}

/** The customer page's tab links: the tabs the session may open, in NAV-038's order. */
export function customerTabLinks(session: Session, orgId: string): TabLink[] {
  return visibleTabs(session, orgId).map((id) => ({ id, label: TAB_TITLES[id], href: customerPath(orgId, id) }));
}

/** The Activity tab with its count of guardrail interventions in the window (R-10); none or 0 leaves the tabs as they are. */
export function withActivityBadge(tabs: readonly TabLink[], triggered: number): TabLink[] {
  return tabs.map((tab) => (tab.id === "activity" && triggered > 0 ? { ...tab, badge: triggered } : tab));
}

/** Only a super-admin reads the org record (name, status, created) and suspends or restores it. */
export function canManageCustomer(session: Session): boolean {
  return session.allows("super-admin");
}

/** NAV-010's installer account console (TASK-122); a plain link, not an API call. */
export function installerAccountPath(orgId: string): string {
  return `/installer/accounts/${encodeURIComponent(orgId)}`;
}

// ---------------------------------------------------------------------------------------------
// SCR-003: the customer list

/**
 * `listOrgs` (API-006) takes no search query, so search filters only the rows already loaded on
 * screen. That is a real limit of the contract, not a bug: this repo does not invent endpoints.
 */
export function filterOrgs(orgs: readonly Org[], search: string): Org[] {
  const needle = search.trim().toLowerCase();
  if (needle === "") return [...orgs];
  return orgs.filter((org) => org.id.toLowerCase().includes(needle) || org.slug.toLowerCase().includes(needle) || org.name.toLowerCase().includes(needle));
}

export type StatusFilter = "all" | "active" | "suspended";

export interface StatusCounts {
  all: number;
  active: number;
  suspended: number;
}

export function statusCounts(orgs: readonly Org[]): StatusCounts {
  return {
    all: orgs.length,
    active: orgs.filter((org) => org.status === "active").length,
    suspended: orgs.filter((org) => org.status === "suspended").length,
  };
}

export function filterByStatus(orgs: readonly Org[], status: StatusFilter): Org[] {
  return status === "all" ? [...orgs] : orgs.filter((org) => org.status === status);
}

export function statusFilterOptions(counts: StatusCounts): { value: StatusFilter; label: string }[] {
  return [
    { value: "all", label: `All ${counts.all}` },
    { value: "active", label: `Active ${counts.active}` },
    { value: "suspended", label: `Suspended ${counts.suspended}` },
  ];
}

/** "Active", "Suspended", "Removed" and their chip colours (Main.dc.html: active green, due amber, retired grey). */
export function orgStatusChip(status: OrgStatus): { label: string; tone: ChipTone } {
  switch (status) {
    case "active":
      return { label: "Active", tone: "green" };
    case "suspended":
      return { label: "Suspended", tone: "amber" };
    case "removed":
      return { label: "Removed", tone: "grey" };
  }
}

/** A customer's "billed this month": the amount, "not billed" (proxy only), or unknown (no row). */
export type BilledFigure = { kind: "billed"; micro: number } | { kind: "not-billed" } | { kind: "unknown" };

/** Reads one customer's figure out of a revenue report (`/v1/admin/revenue/by-org`). */
export function billedFigure(report: RevenueBreakdown | null, orgId: string): BilledFigure {
  if (!report) return { kind: "unknown" };
  const item = report.items.find((row) => row.key === orgId);
  if (item) return { kind: "billed", micro: item.priceMicroUsd };
  if (report.notBilled.some((row) => row.orgId === orgId)) return { kind: "not-billed" };
  return { kind: "unknown" };
}

export interface CustomerRow {
  org: Org;
  /** Requests and tokens over the last 30 days; null when the usage report did not load. */
  requests: number | null;
  tokens: number | null;
  billed: BilledFigure;
}

/** The list's rows: each org with the usage (one `/v1/admin/usage/by-org` call) and this month's
 *  billing (one `/v1/admin/revenue/by-org` call) — never a call per customer. */
export function customerRows(orgs: readonly Org[], usage: readonly OrgUsageItem[] | null, revenue: RevenueBreakdown | null): CustomerRow[] {
  const byOrg = new Map((usage ?? []).map((item) => [item.orgId, item]));
  return orgs.map((org) => {
    const item = byOrg.get(org.id);
    return {
      org,
      requests: usage === null ? null : (item?.requests ?? 0),
      tokens: usage === null ? null : item ? totalTokens(item) : 0,
      billed: billedFigure(revenue, org.id),
    };
  });
}

/** Sort order for the Billed column: amounts by size, then not billed, then unknown. */
export function compareBilled(a: BilledFigure, b: BilledFigure): number {
  const rank = (figure: BilledFigure) => (figure.kind === "billed" ? 0 : figure.kind === "not-billed" ? 1 : 2);
  if (a.kind === "billed" && b.kind === "billed") return a.micro - b.micro;
  return rank(a) - rank(b);
}

export function compareNullable(a: number | null, b: number | null): number {
  return (a ?? -1) - (b ?? -1);
}

const ORG_ID_PATTERN = /^[a-z0-9-]{1,64}$/;

export const orgIdRule: Rule = (value) => (ORG_ID_PATTERN.test(value.trim()) ? null : "Lowercase letters, numbers and hyphens, up to 64 characters.");
export const orgSlugRule: Rule = orgIdRule;
export const orgNameRule: Rule = (value) => {
  const problem = required(value);
  if (problem) return problem;
  return [...value.trim()].length > 200 ? "At most 200 characters." : null;
};

export interface NewOrgValues {
  id: string;
  slug: string;
  name: string;
}

export function validateNewOrg(values: NewOrgValues): FieldErrors {
  return validate(values, { id: [orgIdRule], slug: [orgSlugRule], name: [orgNameRule] });
}

/** `null` when the form does not yet validate; otherwise the trimmed request body. */
export function newOrgRequest(values: NewOrgValues): CreateOrgRequest | null {
  if (hasErrors(validateNewOrg(values))) return null;
  return { id: values.id.trim(), slug: values.slug.trim(), name: values.name.trim() };
}

/** `org_exists` (409) names no field in `details`; the dialog still shows it beside `id`. */
export function orgFormServerError(error: ApiError): FieldErrors {
  if (error.code === "org_exists") return { id: "A customer with this id already exists." };
  return fieldErrorsFrom(error);
}

// ---------------------------------------------------------------------------------------------
// SCR-004 header: status, suspend / restore, the meta line

export interface OrgStatusAction {
  label: "Suspend" | "Restore";
  next: OrgStatus;
}

/** `removed` is terminal (API-006 exposes no way back); there is nothing to offer. */
export function orgStatusAction(org: Org): OrgStatusAction | null {
  if (org.status === "active") return { label: "Suspend", next: "suspended" };
  if (org.status === "suspended") return { label: "Restore", next: "active" };
  return null;
}

export function orgStatusToggleRequest(action: OrgStatusAction): PatchOrgRequest {
  return { status: action.next };
}

export function orgStatusConfirmMessage(org: Org, action: OrgStatusAction): string {
  return action.next === "suspended"
    ? `Suspending "${org.name}" keeps ingest working but blocks credential resolution for every installation.`
    : `Restoring "${org.name}" lets its credentials resolve again.`;
}

/** "Sep 2025": the UTC month the org was created. */
export function sinceMonth(createdAt: string): string {
  return monthLabel(createdAt.slice(0, 7));
}

/** The first month with any usage in a by-month revenue report (its items are keyed YYYY-MM). */
export function firstUsageMonth(items: readonly { key: string; requests: number; tokens: number }[]): string | null {
  const used = items.filter((item) => /^\d{4}-(0[1-9]|1[0-2])$/.test(item.key) && (item.requests > 0 || item.tokens > 0));
  return used.reduce<string | null>((first, item) => (first === null || item.key < first ? item.key : first), null);
}

/**
 * The month a customer became one: the earliest of its first plan assignment and its first usage.
 * The org row's creation date is only the fallback (a customer moved or seeded later would read
 * as new). null when nothing is known.
 */
export function customerSinceMonth(createdAt: string | null | undefined, firsts: readonly (string | null | undefined)[]): string | null {
  const months = firsts.filter((m): m is string => typeof m === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(m)).sort();
  if (months[0]) return months[0];
  return createdAt ? createdAt.slice(0, 7) : null;
}

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
}

/** "25 users" — "500+ users" when the one page read was not the last. */
export function userCountLabel(count: number, more: boolean): string {
  return more ? `${count.toLocaleString("en-US")}+ users` : plural(count, "user", "users");
}

/** Distinct AWS accounts of the customer's live installations (an unknown account is not counted). */
export function awsAccountCount(installations: readonly Installation[]): number {
  return new Set(installations.filter((i) => i.status !== "revoked" && i.awsAccountId).map((i) => i.awsAccountId)).size;
}

export function awsAccountCountLabel(count: number): string {
  return plural(count, "AWS account", "AWS accounts");
}

// ---------------------------------------------------------------------------------------------
// Usage tab: the last 30 days — totals, installations health, usage by day and by model

export const USAGE_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `YYYY-MM-DD` bounds for "usage over the last 30 days" (API-013's `from`/`to`, UTC): the 30 UTC
 * days ending with today. `to` is exclusive and a bare date is its 00:00 UTC, so it is tomorrow —
 * today's date would leave out everything used today.
 */
export function usageWindow(now: Date): { from: string; to: string } {
  const tomorrow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  const to = new Date(tomorrow).toISOString().slice(0, 10);
  const from = new Date(tomorrow - USAGE_WINDOW_DAYS * DAY_MS).toISOString().slice(0, 10);
  return { from, to };
}

export function totalTokens(totals: Pick<UsageTotals, "inputTokens" | "outputTokens" | "cacheReadTokens" | "cacheWriteTokens">): number {
  return totals.inputTokens + totals.outputTokens + totals.cacheReadTokens + totals.cacheWriteTokens;
}

/** Basis points of requests that errored; `null` with no requests in the window (nothing to divide). */
export function errorRateBp(totals: Pick<UsageTotals, "requests" | "errors">): number | null {
  return totals.requests > 0 ? Math.round((totals.errors / totals.requests) * 10_000) : null;
}

export type InstallationHealth = "healthy" | "stale" | "unknown" | "revoked";

/** Generous relative to any expected heartbeat interval: this is an at-a-glance signal, not an alarm. */
const STALE_AFTER_MS = 15 * 60 * 1000;

export function installationHealth(installation: Installation, now: Date): InstallationHealth {
  if (installation.status === "revoked") return "revoked";
  if (!installation.lastSeenAt) return "unknown";
  return now.getTime() - Date.parse(installation.lastSeenAt) <= STALE_AFTER_MS ? "healthy" : "stale";
}

export const HEALTH_CHIPS: Readonly<Record<InstallationHealth, { label: string; tone: ChipTone }>> = {
  healthy: { label: "Healthy", tone: "green" },
  stale: { label: "Stale", tone: "amber" },
  unknown: { label: "No heartbeat yet", tone: "grey" },
  revoked: { label: "Revoked", tone: "grey" },
};

export interface InstallationHealthSummary {
  healthy: number;
  stale: number;
  unknown: number;
  revoked: number;
}

export function installationsHealthSummary(installations: readonly Installation[], now: Date): InstallationHealthSummary {
  const summary: InstallationHealthSummary = { healthy: 0, stale: 0, unknown: 0, revoked: 0 };
  for (const installation of installations) summary[installationHealth(installation, now)]++;
  return summary;
}

/** The date part only (UTC calendar day), so screens never render a locale- or TZ-dependent string. */
export function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

/** "3 Sep 2026" — a UTC calendar day in words. */
export function dayLabel(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${Number(day)} ${MONTH_SHORT_NAMES[Number(month) - 1] ?? month} ${year}`;
}

/** 1234 → "1,234"; 1_250_000 → "1.25M" for a compact card or axis. */
export function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

export function formatCompactCount(value: number): string {
  const magnitude = Math.abs(value);
  const trim = (text: string) => (text.includes(".") ? text.replace(/\.?0+$/, "") : text);
  if (magnitude >= 1_000_000_000) return `${trim((value / 1_000_000_000).toFixed(2))}B`;
  if (magnitude >= 1_000_000) return `${trim((value / 1_000_000).toFixed(2))}M`;
  if (magnitude >= 1_000) return `${trim((value / 1_000).toFixed(1))}k`;
  return String(Math.round(value));
}

export interface DailyUsageRow {
  /** `YYYY-MM-DD` (UTC). */
  day: string;
  requests: number;
  tokens: number;
  costMicroUsd: number | null;
}

/**
 * One row per UTC day of the window, oldest first, from a day-grain time series: a day with no
 * bucket is a zero day (the API sends only buckets with usage). `to` is exclusive. A zero day's
 * cost is $0 only when the series' buckets show cost at all; where the API sends none (the org
 * hides it from this viewer) or no bucket came back, it stays null, so no "$0.00" is invented.
 */
export function dailyUsageRows(points: readonly TimeseriesPoint[], from: string, to: string): DailyUsageRow[] {
  const byDay = new Map(points.map((point) => [point.bucket.slice(0, 10), point]));
  const gapCost = points.some((point) => point.costMicroUsd !== null) ? 0 : null;
  const rows: DailyUsageRow[] = [];
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let time = Date.parse(`${from}T00:00:00Z`); time < end && rows.length < 400; time += DAY_MS) {
    const day = new Date(time).toISOString().slice(0, 10);
    const point = byDay.get(day);
    rows.push(point ? { day, requests: point.requests, tokens: totalTokens(point), costMicroUsd: point.costMicroUsd } : { day, requests: 0, tokens: 0, costMicroUsd: gapCost });
  }
  return rows;
}

/** The chart's bars: tokens per day, the exact figures in each bar's tooltip. */
export function dailyUsageBars(rows: readonly DailyUsageRow[]): ChartBar[] {
  return rows.map((row) => ({
    key: row.day,
    label: String(Number(row.day.slice(8, 10))),
    tooltip: `${dayLabel(row.day)}: ${formatCount(row.tokens)} tokens, ${formatCount(row.requests)} requests${row.costMicroUsd === null ? "" : `, AWS list cost ${formatUsd(row.costMicroUsd)}`}`,
    segments: [{ series: "tokens", value: row.tokens }],
  }));
}

export interface ModelUsageRow {
  model: string;
  requests: number;
  tokens: number;
  costMicroUsd: number | null;
}

/** By-model rows, most tokens first. */
export function modelUsageRows(items: readonly ModelUsageItem[]): ModelUsageRow[] {
  return items
    .map((item) => ({ model: item.bedrockModelId, requests: item.requests, tokens: totalTokens(item), costMicroUsd: item.costMicroUsd }))
    .sort((a, b) => b.tokens - a.tokens || a.model.localeCompare(b.model));
}

// ---------------------------------------------------------------------------------------------
// Installations tab

const AWS_ACCOUNT_ID_PATTERN = /^[0-9]{12}$/;

export const installationNameRule: Rule = (value) => {
  const problem = required(value);
  if (problem) return problem;
  return [...value.trim()].length > 100 ? "At most 100 characters." : null;
};

/** Optional field: blank is fine, present must be a 12-digit AWS account id. */
export const awsAccountIdRule: Rule = (value) => (value.trim() === "" || AWS_ACCOUNT_ID_PATTERN.test(value.trim()) ? null : "A 12-digit AWS account id.");

export interface NewInstallationValues {
  name: string;
  awsAccountId: string;
  awsRegion: string;
}

export function validateNewInstallation(values: NewInstallationValues): FieldErrors {
  return validate(values, { name: [installationNameRule], awsAccountId: [awsAccountIdRule] });
}

export function newInstallationRequest(values: NewInstallationValues): CreateInstallationRequest | null {
  if (hasErrors(validateNewInstallation(values))) return null;
  const request: CreateInstallationRequest = { name: values.name.trim() };
  if (values.awsAccountId.trim() !== "") request.awsAccountId = values.awsAccountId.trim();
  if (values.awsRegion.trim() !== "") request.awsRegion = values.awsRegion.trim();
  return request;
}

export function installationFormServerError(error: ApiError): FieldErrors {
  if (error.code === "installation_exists") return { name: "An installation with this name already exists." };
  return fieldErrorsFrom(error);
}

export function canRotateInstallationSecret(installation: Installation): boolean {
  return installation.status === "active" && !installation.rotationPending;
}

export function canPromoteInstallationSecret(installation: Installation): boolean {
  return installation.status === "active" && installation.rotationPending;
}

export function canRevokeInstallation(installation: Installation): boolean {
  return installation.status === "active";
}

// ---------------------------------------------------------------------------------------------
// OIDC providers tab

export const httpsUrlRule: Rule = (value) => {
  const trimmed = value.trim();
  if (trimmed === "") return "Required.";
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return "A valid URL.";
  }
  return url.protocol === "https:" ? null : "Must be an https URL.";
};

export const audienceRule: Rule = required;

export interface NewOidcProviderValues {
  issuer: string;
  audience: string;
  jwksUri: string;
}

export function validateOidcProvider(values: Pick<NewOidcProviderValues, "issuer" | "audience">): FieldErrors {
  return validate(values, { issuer: [httpsUrlRule], audience: [audienceRule] });
}

export function newOidcProviderRequest(values: NewOidcProviderValues): CreateOidcProviderRequest | null {
  if (hasErrors(validateOidcProvider(values))) return null;
  const request: CreateOidcProviderRequest = { issuer: values.issuer.trim(), audience: values.audience.trim(), enabled: true };
  if (values.jwksUri.trim() !== "") request.jwksUri = values.jwksUri.trim();
  return request;
}

export function setOidcProviderEnabled(enabled: boolean): PatchOidcProviderRequest {
  return { enabled };
}

export function oidcFormServerError(error: ApiError): FieldErrors {
  if (error.code === "issuer_unreachable") return { issuer: "The issuer could not be reached for discovery." };
  if (error.code === "provider_exists") return { issuer: "A provider with this issuer is already registered." };
  return fieldErrorsFrom(error);
}

// ---------------------------------------------------------------------------------------------
// AWS sign-in tab: which AWS principals the org's proxies admit with an `nxa_` token, and which
// Nexus team each lands in. The installation's own account is always admitted by the proxy, so
// it never needs to be listed here. Limits mirror the API's (the server re-validates).

export const AWS_SIGN_IN_MAX_ACCOUNTS = 100;
export const AWS_SIGN_IN_MAX_PRINCIPALS = 100;
export const AWS_SIGN_IN_MAX_TEAM_MAPPINGS = 200;
const TEAM_EXTERNAL_ID_MAX = 200;
const AWS_PRINCIPAL_PATTERN = /^(role|permission-set|user|federated-user)\/[A-Za-z0-9+=,.@_*-]{1,128}$/;

export interface AwsTeamMappingValues {
  match: string;
  teamExternalId: string;
}

/** The form: accounts and principal patterns one per line, team mappings as rows. */
export interface AwsSignInValues {
  allowedAccounts: string;
  allowedPrincipals: string;
  teamMappings: AwsTeamMappingValues[];
}

/** The non-blank lines of a textarea, trimmed. */
export function nonBlankLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function awsSignInFormFrom(settings: AwsSignInSettings): AwsSignInValues {
  return {
    allowedAccounts: settings.allowedAccounts.join("\n"),
    allowedPrincipals: settings.allowedPrincipals.join("\n"),
    teamMappings: settings.teamMappings.map((mapping) => ({ match: mapping.match, teamExternalId: mapping.teamExternalId })),
  };
}

export const awsPrincipalPatternRule: Rule = (value) =>
  AWS_PRINCIPAL_PATTERN.test(value.trim())
    ? null
    : "role/…, permission-set/…, user/… or federated-user/… followed by a name (letters, digits, +=,.@_- and * as a wildcard).";

function firstDuplicate(values: readonly string[]): string | null {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) return value;
    seen.add(value);
  }
  return null;
}

function listProblem(values: readonly string[], max: number, noun: string, rule: Rule): string | null {
  if (values.length > max) return `At most ${max} ${noun}.`;
  for (const value of values) {
    const problem = rule(value);
    if (problem !== null) return `${value}: ${problem}`;
  }
  const duplicate = firstDuplicate(values);
  return duplicate === null ? null : `${duplicate} is listed twice.`;
}

/** A mapping row with both cells blank is an unused row, not an error; it is dropped on save. */
function usedMappings(values: AwsSignInValues): { row: AwsTeamMappingValues; index: number }[] {
  return values.teamMappings
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.match.trim() !== "" || row.teamExternalId.trim() !== "");
}

export function validateAwsSignIn(values: AwsSignInValues): FieldErrors {
  const errors: FieldErrors = {};
  const accounts = listProblem(nonBlankLines(values.allowedAccounts), AWS_SIGN_IN_MAX_ACCOUNTS, "accounts", (value) =>
    AWS_ACCOUNT_ID_PATTERN.test(value) ? null : "not a 12-digit AWS account id.",
  );
  if (accounts !== null) errors.allowedAccounts = accounts;
  const principals = listProblem(nonBlankLines(values.allowedPrincipals), AWS_SIGN_IN_MAX_PRINCIPALS, "principal patterns", awsPrincipalPatternRule);
  if (principals !== null) errors.allowedPrincipals = principals;
  const mappings = usedMappings(values);
  if (mappings.length > AWS_SIGN_IN_MAX_TEAM_MAPPINGS) errors.teamMappings = `At most ${AWS_SIGN_IN_MAX_TEAM_MAPPINGS} team mappings.`;
  for (const { row, index } of mappings) {
    const match = awsPrincipalPatternRule(row.match);
    if (match !== null) errors[`teamMappings.${index}.match`] = match;
    const team = row.teamExternalId.trim();
    if (team === "") errors[`teamMappings.${index}.teamExternalId`] = "Required.";
    else if (team.length > TEAM_EXTERNAL_ID_MAX) errors[`teamMappings.${index}.teamExternalId`] = `At most ${TEAM_EXTERNAL_ID_MAX} characters.`;
  }
  return errors;
}

/** The PUT body, or null while the form has errors. */
export function awsSignInRequest(values: AwsSignInValues): AwsSignInSettings | null {
  if (hasErrors(validateAwsSignIn(values))) return null;
  return {
    allowedAccounts: nonBlankLines(values.allowedAccounts),
    allowedPrincipals: nonBlankLines(values.allowedPrincipals),
    teamMappings: usedMappings(values).map(({ row }) => ({ match: row.match.trim(), teamExternalId: row.teamExternalId.trim() })),
  };
}

export function awsSignInServerError(error: ApiError): FieldErrors {
  return fieldErrorsFrom(error);
}

// ---------------------------------------------------------------------------------------------
// Settings tab: prompt logging, retention, cost visibility

export const promptRetentionDaysRule: Rule = integerBetween(1, 3650);
export const usageRetentionDaysRule: Rule = integerBetween(30, 3650);

export interface OrgSettingsValues {
  promptRetentionDays: string;
  usageRetentionDays: string;
}

export function validateOrgSettingsForm(values: OrgSettingsValues): FieldErrors {
  return validate(values, { promptRetentionDays: [promptRetentionDaysRule], usageRetentionDays: [usageRetentionDaysRule] });
}

export function patchOrgSettingsRequest(promptLoggingEnabled: boolean, values: OrgSettingsValues): PatchOrgSettingsRequest | null {
  if (hasErrors(validateOrgSettingsForm(values))) return null;
  return {
    promptLoggingEnabled,
    promptRetentionDays: Number(values.promptRetentionDays.trim()),
    usageRetentionDays: Number(values.usageRetentionDays.trim()),
  };
}

/** Cost visibility is read-only wherever it appears (SCR-004): org-admins see it, nobody without `billing:write` can change it. */
export function canViewCostVisibility(session: Session): boolean {
  return session.me.role === "org-admin" || session.can("billing:write");
}

/** The customer's Assistant switch (API-012 `assistantEnabled`): only a super-admin may send it, so only one sees it. */
export function canSwitchAssistant(session: Session): boolean {
  return session.allows("super-admin");
}

export function canEditCostVisibility(session: Session): boolean {
  return session.can("billing:write");
}

export function putCostVisibilityRequest(costVisibility: CostVisibility): PutOrgCostVisibilityRequest {
  return { costVisibility };
}
